import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTheme, fonts } from "@/theme";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/lib/toast";
import { CrystalVisual } from "@/components/CrystalVisual";
import { crystalsApi, pushRecentCrystal } from "@/data/api";
import { INTENTION_ICON, RITZYRA_APPROACH } from "@/data/crystalsData";
import { Screen, TopBar, Loader } from "@/components/ui";

function Acc({ title, icon, children, defaultOpen, testId }) {
  const { colors } = useTheme();
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div style={{ background: colors.surfaceSecondary, borderRadius: 16, border: `1px solid ${colors.border}`, marginBottom: 12, overflow: "hidden" }}>
      <button data-testid={testId} className="pressable" onClick={() => setOpen((o) => !o)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: 16, background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}>
        {icon ? <Icon name={icon} size={18} color={colors.brandSecondary} /> : null}
        <span style={{ flex: 1, color: colors.onSurface, fontSize: 16, fontWeight: 700, fontFamily: fonts.text }}>{title}</span>
        <Icon name={open ? "chevron-up" : "chevron-down"} size={20} color={colors.muted} />
      </button>
      {open ? <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 10 }}>{children}</div> : null}
    </div>
  );
}

export default function CrystalDetail() {
  const { colors } = useTheme();
  const toast = useToast();
  const { token } = useAuth();
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fav, setFav] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    crystalsApi.get(slug).then((crys) => {
      setC(crys);
      pushRecentCrystal({ slug: crys.slug, name: crys.name, visual_seed: crys.visual_seed });
    }).catch(() => setC(null)).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!token || !c) return;
    crystalsApi.favourites(token).then((r) => setFav(r.items.some((i) => i.id === c.id))).catch(() => {});
  }, [token, c]);

  const toggleFav = async () => {
    if (!c || !token) return;
    const next = !fav;
    setFav(next);
    try {
      if (next) { await crystalsApi.addFavourite(c.id, token); toast.show("Saved to My Crystals ✨"); }
      else { await crystalsApi.removeFavourite(c.id, token); toast.show("Removed from My Crystals"); }
    } catch { setFav(!next); toast.show("Couldn't update — try again"); }
  };

  if (loading) return <Screen><TopBar title="Crystal" /><Loader /></Screen>;
  if (!c) return <Screen><TopBar title="Crystal" /><div style={{ padding: 40, textAlign: "center", color: colors.muted, fontFamily: fonts.text }}>Couldn't find that crystal.</div></Screen>;

  const quickRef = [
    c.name,
    c.what_is ? `Geology: ${c.what_is}` : "",
    c.spiritual?.length ? `Spiritual associations:\n- ${c.spiritual.slice(0, 3).join("\n- ")}` : "",
    c.quick_fact ? `Did you know: ${c.quick_fact}` : "",
    c.content_angles?.length ? `Content hooks:\n- ${c.content_angles.slice(0, 3).join("\n- ")}` : "",
    "Remember: put it into your own words 🖤",
  ].filter(Boolean).join("\n\n");

  const body = (t) => <div style={{ color: colors.onSurfaceSecondary, fontSize: 14, lineHeight: "21px", fontFamily: fonts.text, whiteSpace: "pre-wrap" }}>{t}</div>;

  return (
    <Screen>
      <TopBar title={c.name} />
      <div className="ritzy-fade" style={{ padding: "16px 20px 40px" }}>
        <div style={{ position: "relative", marginBottom: 14 }}>
          <CrystalVisual name={c.name} imageUrl={c.image_url} seed={c.visual_seed} height={190} showName={false} />
          <button data-testid="favourite-button" className="pressable" onClick={toggleFav} style={{ position: "absolute", top: 12, right: 12, width: 40, height: 40, borderRadius: 20, background: colors.surface, border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <Icon name="heart" size={22} color={fav ? colors.error : colors.muted} />
          </button>
        </div>

        <div style={{ color: colors.onSurface, fontSize: 26, fontFamily: fonts.display, fontWeight: 700 }}>{c.name}</div>
        {c.aliases?.length ? <div style={{ color: colors.muted, fontSize: 13, marginTop: 4, fontStyle: "italic", fontFamily: fonts.text }}>Also known as: {c.aliases.join(", ")}</div> : null}

        <button data-testid="create-content-button" className="pressable" onClick={() => setShowContent(true)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: colors.brand, color: colors.onBrand, border: "none", borderRadius: 14, padding: "13px", margin: "14px 0 16px", cursor: "pointer", fontSize: 15, fontWeight: 700, fontFamily: fonts.text }}>
          <Icon name="smartphone" size={16} color={colors.onBrand} />Create Content 📱
        </button>

        {c.what_is ? <Acc title="What is it? 🪨" icon="info" defaultOpen testId="acc-whatis">{body(c.what_is)}</Acc> : null}
        {c.look_for ? <Acc title="Look for 👀" icon="eye" testId="acc-lookfor">{body(c.look_for)}</Acc> : null}
        {c.spiritual?.length ? <Acc title="Spiritual associations 🔮" icon="star" testId="acc-spiritual">{c.spiritual.map((s, i) => <div key={i} style={{ color: colors.onSurfaceSecondary, fontSize: 14, lineHeight: "21px", fontFamily: fonts.text }}>• {s}</div>)}</Acc> : null}
        {c.folklore ? <Acc title="Folklore & history 📖" icon="feather" testId="acc-folklore">{body(c.folklore)}</Acc> : null}

        {c.quick_fact ? (
          <div style={{ background: colors.brandTertiary, borderRadius: 16, padding: 16, marginBottom: 12 }}>
            <div style={{ color: colors.onBrandTertiary, fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: fonts.text }}>Quick fact 👀</div>
            <div style={{ color: colors.onBrandTertiary, fontSize: 14, lineHeight: "21px", marginTop: 6, fontFamily: fonts.text }}>{c.quick_fact}</div>
          </div>
        ) : null}

        {c.content_angles?.length ? <Acc title="Content angles 📱" icon="video" defaultOpen testId="acc-angles">{c.content_angles.map((a, i) => <div key={i} style={{ color: colors.onSurfaceSecondary, fontSize: 14, lineHeight: "21px", fontFamily: fonts.text }}>• {a}</div>)}</Acc> : null}

        {c.intentions?.length ? (
          <>
            <div style={{ color: colors.onSurface, fontSize: 15, fontWeight: 700, margin: "4px 0 10px", fontFamily: fonts.text }}>Good for content about ✨</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
              {c.intentions.map((t) => (
                <span key={t} style={{ display: "flex", alignItems: "center", gap: 6, background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 999, padding: "7px 12px", color: colors.onSurface, fontSize: 13, fontWeight: 600, fontFamily: fonts.text }}>
                  <Icon name={INTENTION_ICON[t] ?? "star"} size={13} color={colors.brand} />{t}
                </span>
              ))}
            </div>
          </>
        ) : null}

        {c.keep_accurate ? (
          <div style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.warning}`, borderRadius: 16, padding: 16, marginTop: 12 }}>
            <div style={{ color: colors.onSurface, fontSize: 13, fontWeight: 800, fontFamily: fonts.text }}>Keep it accurate ⚠️</div>
            <div style={{ color: colors.onSurfaceSecondary, fontSize: 14, lineHeight: "21px", marginTop: 6, fontFamily: fonts.text }}>{c.keep_accurate}</div>
          </div>
        ) : null}

        <div style={{ background: colors.brandTertiary, borderRadius: 16, padding: 16, marginTop: 16 }}>
          <div style={{ color: colors.onBrandTertiary, fontSize: 15, fontFamily: fonts.display, fontWeight: 700 }}>{RITZYRA_APPROACH.title}</div>
          <div style={{ color: colors.onBrandTertiary, fontSize: 13, lineHeight: "20px", marginTop: 6, fontFamily: fonts.text }}>{RITZYRA_APPROACH.lines[0]}</div>
        </div>
      </div>

      {showContent ? (
        <div onClick={() => setShowContent(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 50 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 720, background: colors.surface, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 20, maxHeight: "80vh", display: "flex", flexDirection: "column" }} className="ritzy-fade">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ flex: 1, color: colors.onSurface, fontSize: 18, fontFamily: fonts.display, fontWeight: 700 }}>Quick reference — {c.name}</div>
              <button data-testid="close-content" onClick={() => setShowContent(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><Icon name="x" size={22} color={colors.onSurface} /></button>
            </div>
            <div style={{ overflowY: "auto", marginBottom: 12, flex: 1 }}>
              {c.what_is ? <div style={{ marginBottom: 14 }}><div style={{ color: colors.brandSecondary, fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6, fontFamily: fonts.text }}>One geology fact</div><div style={{ color: colors.onSurfaceSecondary, fontSize: 14, lineHeight: "21px", fontFamily: fonts.text }}>{c.quick_fact || c.what_is}</div></div> : null}
              {c.spiritual?.length ? <div style={{ marginBottom: 14 }}><div style={{ color: colors.brandSecondary, fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6, fontFamily: fonts.text }}>3 spiritual associations</div>{c.spiritual.slice(0, 3).map((s, i) => <div key={i} style={{ color: colors.onSurfaceSecondary, fontSize: 14, lineHeight: "21px", fontFamily: fonts.text }}>• {s}</div>)}</div> : null}
              {c.content_angles?.length ? <div style={{ marginBottom: 14 }}><div style={{ color: colors.brandSecondary, fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6, fontFamily: fonts.text }}>3 content hooks</div>{c.content_angles.slice(0, 3).map((a, i) => <div key={i} style={{ color: colors.onSurfaceSecondary, fontSize: 14, lineHeight: "21px", fontFamily: fonts.text }}>• {a}</div>)}</div> : null}
              <div style={{ color: colors.muted, fontSize: 13, fontStyle: "italic", marginTop: 4, fontFamily: fonts.text }}>Now put it into your own words 🖤</div>
            </div>
            <button data-testid="copy-quickref" className="pressable" onClick={async () => { try { await navigator.clipboard.writeText(quickRef); toast.show("Copied ✨"); } catch { toast.show("Couldn't copy"); } }} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: colors.brand, color: colors.onBrand, border: "none", borderRadius: 14, padding: "14px", cursor: "pointer", fontSize: 15, fontWeight: 700, fontFamily: fonts.text }}>
              <Icon name="copy" size={16} color={colors.onBrand} />Copy all
            </button>
          </div>
        </div>
      ) : null}
    </Screen>
  );
}
